/**
 * AiChatSidebar — Right panel with chat UI for AI interaction.
 *
 * Includes: header with reset/close, message list, and input area.
 * Resizable via left drag handle.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { X, RotateCcw, AlertTriangle, CheckCircle, Download, Monitor, Terminal, Info } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { useAiChatStore, defaultTabState, DEFAULT_SIDEBAR_WIDTH } from '../store/aiChatStore';
import { useSplitterDrag } from '../../../layout/hooks/useSplitterDrag';
import { useAnalytics } from '../../../core/analytics/useAnalytics';
import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import { AiChatMessages } from './AiChatMessages';
import { AiChatInput } from './AiChatInput';
import { AiChatCliSelector } from './AiChatCliSelector';
import { AiChatSwitchConfirmDialog } from './AiChatSwitchConfirmDialog';
import { DebugToolConfirmation } from './DebugToolConfirmation';
import { isBrowserMode, PRODUCT_NAME } from '../../browser-mode/browserModeUtils';
import { useSettingsDialogStore } from '../../main-toolbar/store/settingsDialogStore';
import { convertHistoryToMessages } from '../utils/historyConverter';
import type { DatabaseObject } from '../../database-objects/models/databaseObjectTypes';

type AdapterInfo = { name: string; displayName: string };

/** Poll MCP server status. Returns current running state and whether it was just enabled. */
function useMcpRunning(): { running: boolean | undefined; justEnabled: boolean } {
  const [running, setRunning] = useState<boolean | undefined>(undefined);
  const [justEnabled, setJustEnabled] = useState(false);
  const prevRunningRef = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    const api = window.electronAPI?.mcpServer;
    if (!api) { setRunning(false); return; }
    const check = () => {
      api.getStatusLight().then((result: { success?: boolean; status?: { running?: boolean } }) => {
        const isRunning = result.success ? (result.status?.running ?? false) : false;
        setRunning(isRunning);
        if (isRunning && prevRunningRef.current === false) {
          setJustEnabled(true);
        }
        if (!isRunning) {
          setJustEnabled(false);
        }
        prevRunningRef.current = isRunning;
      }).catch(() => setRunning(false));
    };
    check();
    const interval = setInterval(check, 5000);
    return () => clearInterval(interval);
  }, []);
  return { running, justEnabled };
}

type AiChatSidebarProps = {
  tabId: string;
  onClose: () => void;
  parentWidth: number;
  databaseObjects?: DatabaseObject[];
  onSendMessage: (message: string) => void;
  onCancel: () => void;
  connectionId?: string;
  database?: string;
  engine?: string;
};

const MIN_WIDTH = 250;

/** Keyboard resize step for the AI chat sidebar width, in pixels (Shift = 4x). */
const AI_CHAT_KEY_STEP = 8;
const AI_CHAT_KEY_STEP_BIG = 32;

const CODEX_HINT_DISMISSED_KEY = 'ai-chat-codex-hint-dismissed';

/**
 * Remember the Codex hint's dismissal across launches. A raw `setItem` throws
 * `QuotaExceededError` once the origin is full, and a throw inside a React
 * event handler is an uncaught renderer crash — the same class the persisted
 * stores route through `core/storage/guardedJsonStorage.ts`. The hint stays
 * dismissed for this session from component state either way.
 */
function dismissCodexHintPersistently(): void {
  try {
    localStorage.setItem(CODEX_HINT_DISMISSED_KEY, '1');
  } catch {
    // Storage full or unavailable — worst case the hint returns next launch.
  }
}

export function AiChatSidebar({ tabId, onClose, parentWidth, databaseObjects, onSendMessage, onCancel, connectionId, database, engine }: AiChatSidebarProps) {
  const tabState = useAiChatStore((s) => s.tabs[tabId]) ?? defaultTabState();
  const { running: mcpRunning, justEnabled: mcpJustEnabled } = useMcpRunning();
  const setSidebarWidth = useAiChatStore((s) => s.setSidebarWidth);
  const clearMessages = useAiChatStore((s) => s.clearMessages);
  const setMessages = useAiChatStore((s) => s.setMessages);
  const switchAdapter = useAiChatStore((s) => s.switchAdapter);
  const setActiveAdapter = useAiChatStore((s) => s.setActiveAdapter);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [availableAdapters, setAvailableAdapters] = useState<AdapterInfo[]>([]);
  const [switchTarget, setSwitchTarget] = useState<AdapterInfo | null>(null);
  const [codexHintDismissed, setCodexHintDismissed] = useState(() => localStorage.getItem(CODEX_HINT_DISMISSED_KEY) === '1');
  const [pendingConfirmations, setPendingConfirmations] = useState<Array<{
    requestId: string;
    toolName: string;
    description: string;
    category: string;
    // Carried through because the "remember for this session" grant is keyed on
    // it: without a tabId the main process silently discards the choice, and a
    // checkbox that does nothing is worse than no checkbox.
    tabId?: string;
  }>>([]);

  useEffect(() => {
    const cleanup = window.electronAPI?.debugConfirmation?.onRequest((request) => {
      setPendingConfirmations(prev => [...prev, request]);
    });
    return () => cleanup?.();
  }, []);

  const handleConfirmationResponse = useCallback((requestId: string, approved: boolean, remember: boolean) => {
    window.electronAPI?.debugConfirmation?.sendResponse({ requestId, approved, rememberForSession: remember });
    setPendingConfirmations(prev => prev.filter(c => c.requestId !== requestId));
  }, []);

  const maxWidth = Math.floor(parentWidth * 0.5);
  const width = Math.max(MIN_WIDTH, Math.min(tabState.sidebarWidth, maxWidth));
  const { track } = useAnalytics();

  const focusChatInput = useCallback(() => {
    requestAnimationFrame(() => {
      document.querySelector<HTMLTextAreaElement>('[data-testid="ai-chat-textarea"]')?.focus();
    });
  }, []);

  // Fetch available adapters and resolve activeAdapter on mount
  useEffect(() => {
    const api = window.electronAPI?.aiChat;
    if (!api) return;
    (async () => {
      const [adapters, preferred] = await Promise.all([
        api.getAvailableAdapters(),
        api.getPreferredAdapter(),
      ]);
      setAvailableAdapters(adapters);
      // Initialize activeAdapter if not set yet
      if (!tabState.activeAdapter && adapters.length > 0) {
        const resolved = (preferred && adapters.some((a) => a.name === preferred))
          ? preferred
          : adapters[0].name;
        setActiveAdapter(tabId, resolved);
      }
    })();
  }, [tabId, tabState.activeAdapter, setActiveAdapter]);

  const activeAdapterName = tabState.activeAdapter ?? availableAdapters[0]?.name ?? 'claude';
  const activeAdapterDisplay = availableAdapters.find((a) => a.name === activeAdapterName)?.displayName ?? activeAdapterName;

  const handleSwitchRequest = useCallback((targetName: string) => {
    const target = availableAdapters.find((a) => a.name === targetName);
    if (!target) return;
    // No messages to lose — switch immediately without confirmation
    if (tabState.messages.length === 0 && !tabState.sessionId) {
      switchAdapter(tabId, target.name);
      useWorkspaceStore.getState().updateTabContent(tabId, { aiChatSessionId: undefined });
      focusChatInput();
      return;
    }
    setSwitchTarget(target);
  }, [availableAdapters, tabState.messages.length, tabState.sessionId, tabId, switchAdapter, focusChatInput]);

  const handleSwitchConfirm = useCallback(() => {
    if (!switchTarget) return;
    if (tabState.isLoading) {
      onCancel();
    }
    switchAdapter(tabId, switchTarget.name);
    useWorkspaceStore.getState().updateTabContent(tabId, { aiChatSessionId: undefined });
    setSwitchTarget(null);
    focusChatInput();
  }, [switchTarget, tabId, tabState.isLoading, onCancel, switchAdapter, focusChatInput]);

  const handleSwitchCancel = useCallback(() => {
    setSwitchTarget(null);
    focusChatInput();
  }, [focusChatInput]);

  const hasConversation = tabState.messages.length > 0 || !!tabState.sessionId;

  const handleResetClick = useCallback(() => {
    if (!hasConversation) return;
    setShowResetConfirm(true);
  }, [hasConversation]);

  const handleResetConfirm = useCallback(() => {
    setShowResetConfirm(false);
    if (tabState.isLoading) {
      onCancel();
    }
    clearMessages(tabId);
    useWorkspaceStore.getState().updateTabContent(tabId, { aiChatSessionId: undefined });
    focusChatInput();
  }, [clearMessages, tabId, tabState.isLoading, onCancel, focusChatInput]);

  const handleResetCancel = useCallback(() => {
    setShowResetConfirm(false);
    focusChatInput();
  }, [focusChatInput]);

  const handleLoadHistory = useCallback(async () => {
    const api = window.electronAPI?.aiChat;
    if (!api || !tabState.sessionId) return;
    setIsLoadingHistory(true);
    try {
      const result = await api.loadHistory(tabState.sessionId);
      if (result.success && result.messages.length > 0) {
        const msgs = convertHistoryToMessages(result.messages);
        setMessages(tabId, msgs);
      }
    } catch {
      /* ignore */
    } finally {
      setIsLoadingHistory(false);
    }
  }, [tabId, tabState.sessionId, setMessages]);

  // Resize handle (CH-13): pointer-capture + rAF drag, keyboard step,
  // double-click reset, via the shared `useSplitterDrag` hook. The panel
  // div's width is mutated directly during the drag (see `onDrag`) and only
  // committed to the store on drag end, matching the pre-existing
  // mouse-drag behaviour this replaces (which drove `setSidebarWidth` from
  // `document`-level mousemove listeners). `maxWidth` depends on the
  // dynamic `parentWidth` prop, so it — like the start width — is captured
  // once at drag start (mirrors the original closure's behaviour, where
  // `handleMouseDown` was rebuilt via `useCallback([width, maxWidth, ...])`
  // on every width/maxWidth change).
  const sidebarRef = useRef<HTMLDivElement>(null);
  const aiChatHandleRef = useRef<HTMLDivElement>(null);
  const aiChatDragStartWidthRef = useRef(width);
  const aiChatMaxWidthRef = useRef(maxWidth);
  const aiChatSplitter = useSplitterDrag({
    axis: 'x',
    onDrag: (deltaPx) => {
      const el = sidebarRef.current;
      if (!el) return;
      // Dragging left increases width (this is the RIGHT-side panel's left
      // edge) — mirrors the original `startX - clientX` delta.
      const newWidth = Math.max(
        MIN_WIDTH,
        Math.min(aiChatDragStartWidthRef.current - deltaPx, aiChatMaxWidthRef.current)
      );
      el.style.width = `${newWidth}px`;
      const handle = aiChatHandleRef.current;
      if (handle) {
        const rounded = Math.round(newWidth);
        handle.setAttribute('aria-valuenow', String(rounded));
        handle.setAttribute('aria-valuetext', `${rounded} pixels`);
      }
    },
    onDragEnd: () => {
      const el = sidebarRef.current;
      if (el) setSidebarWidth(tabId, el.getBoundingClientRect().width);
    },
    onReset: () => {
      setSidebarWidth(tabId, DEFAULT_SIDEBAR_WIDTH);
      track('feature:splitter_reset', { surface: 'ai-chat-sidebar' });
    },
    onKeyStep: (direction, big) => {
      const step = big ? AI_CHAT_KEY_STEP_BIG : AI_CHAT_KEY_STEP;
      setSidebarWidth(tabId, Math.max(MIN_WIDTH, Math.min(width + direction * step, maxWidth)));
    },
  });

  // Show resume notice when sessionId exists but no messages in memory
  const hasSessionId = !!tabState.sessionId;
  const hasNoMessages = tabState.messages.length === 0;
  const showResumeNotice = hasSessionId && hasNoMessages;

  return (
    <div
      ref={sidebarRef}
      className="flex h-full shrink-0"
      style={{ width }}
      data-testid="ai-chat-sidebar"
    >
      {/* Resize handle. CH-13: 1px `--divider` hairline + 6px invisible hit
          area, hover/drag pinned to the identical `--primary` tint,
          pointer-capture + rAF drag, keyboard resize, double-click reset,
          `role="separator"`. */}
      <div
        ref={aiChatHandleRef}
        className="w-1.5 h-full cursor-col-resize flex items-center justify-center shrink-0 bg-transparent before:absolute before:inset-y-0 before:left-0 before:w-px before:bg-divider before:transition-colors before:content-[''] hover:before:bg-primary/60 data-[dragging=true]:before:bg-primary/60 relative outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
        style={{ touchAction: 'none', userSelect: 'none' }}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize AI chat sidebar"
        aria-valuenow={Math.round(width)}
        aria-valuemin={MIN_WIDTH}
        aria-valuemax={maxWidth}
        aria-valuetext={`${Math.round(width)} pixels`}
        tabIndex={0}
        data-dragging={aiChatSplitter.isDragging ? 'true' : undefined}
        data-testid="ai-chat-resize-handle"
        onPointerDown={(e) => {
          aiChatDragStartWidthRef.current = width;
          aiChatMaxWidthRef.current = maxWidth;
          aiChatSplitter.onPointerDown(e);
        }}
        onDoubleClick={aiChatSplitter.onDoubleClick}
        onKeyDown={aiChatSplitter.onKeyDown}
      >
        <div className="w-0.5 h-8 bg-border rounded-full" />
      </div>

      {/* Sidebar content */}
      <div className="flex-1 flex flex-col min-w-0 bg-background">
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-1.5 border-b border-border shrink-0 select-none">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-medium whitespace-nowrap" data-testid="ai-chat-header">
              {availableAdapters.length <= 1 ? `AI Chat · ${activeAdapterDisplay}` : 'AI Chat'}
            </span>
            <AiChatCliSelector
              adapters={availableAdapters}
              activeAdapter={activeAdapterName}
              isLoading={tabState.isLoading}
              onSwitch={handleSwitchRequest}
            />
          </div>
          <div className="flex items-center gap-0.5 shrink-0">
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={handleResetClick}
              disabled={!hasConversation}
              title="Reset conversation"
              data-testid="ai-chat-reset-button"
            >
              <RotateCcw className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={onClose}
              title="Close sidebar"
              data-testid="ai-chat-close-button"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {isBrowserMode() ? (
          <AiChatBrowserModeCTA />
        ) : (<>
          {/* MCP status notice */}
          {mcpRunning === false && (
            <div
              className="flex items-center gap-1.5 px-3 py-1.5 border-b border-border text-xs text-warning bg-amber-50 dark:bg-amber-950/30 shrink-0"
              data-testid="ai-chat-mcp-notice"
            >
              <AlertTriangle className="h-3 w-3 shrink-0" />
              <span>MCP disabled — AI cannot use database tools</span>
              <button
                className="ml-auto underline hover:text-amber-700 dark:hover:text-amber-300 whitespace-nowrap cursor-pointer"
                onClick={() => useSettingsDialogStore.getState().openToSection('ai-integrations')}
                data-testid="ai-chat-enable-mcp-btn"
              >
                Enable MCP
              </button>
            </div>
          )}
          {mcpJustEnabled && (
            <div
              className="flex items-center gap-1.5 px-3 py-1.5 border-b border-border text-xs text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950/30 shrink-0"
              data-testid="ai-chat-mcp-enabled-notice"
            >
              <CheckCircle className="h-3 w-3 shrink-0" />
              <span>MCP enabled — start a new chat to use database tools</span>
              <button
                className="ml-auto underline hover:text-green-700 dark:hover:text-green-300 whitespace-nowrap cursor-pointer"
                onClick={() => { clearMessages(tabId); }}
                data-testid="ai-chat-new-chat-btn"
              >
                New chat
              </button>
            </div>
          )}

          {/* Codex model hint — shown once when Codex is first selected */}
          {activeAdapterName === 'codex' && !codexHintDismissed && (
            <div
              className="flex items-center gap-1.5 px-3 py-1.5 border-b border-border text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/30 shrink-0"
              data-testid="ai-chat-codex-hint"
            >
              <Info className="h-3 w-3 shrink-0" />
              <span>Uses the model configured in your Codex CLI</span>
              <button
                className="ml-auto text-blue-400 dark:text-blue-500 hover:text-blue-600 dark:hover:text-blue-300 cursor-pointer"
                onClick={() => { setCodexHintDismissed(true); dismissCodexHintPersistently(); }}
                data-testid="ai-chat-codex-hint-dismiss"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          )}

          {/* Messages */}
          <AiChatMessages
            messages={tabState.messages}
            isLoading={tabState.isLoading}
            showResumeNotice={showResumeNotice}
            onStartFresh={handleResetConfirm}
            onLoadHistory={handleLoadHistory}
            isLoadingHistory={isLoadingHistory}
            statusText={tabState.statusText}
            phase={tabState.phase}
            lastRunMetadata={tabState.lastRunMetadata}
          />

          {/* Debug tool confirmations */}
          {pendingConfirmations.map(conf => (
            <DebugToolConfirmation
              key={conf.requestId}
              requestId={conf.requestId}
              toolName={conf.toolName}
              description={conf.description}
              category={conf.category}
              canRemember={conf.tabId !== undefined}
              onRespond={handleConfirmationResponse}
            />
          ))}

          {/* Input */}
          <AiChatInput
            onSend={onSendMessage}
            onCancel={onCancel}
            isLoading={tabState.isLoading}
            databaseObjects={databaseObjects}
            connectionId={connectionId}
            database={database}
            engine={engine}
          />
        </>)}

        {/* Reset confirmation dialog */}
        <AlertDialog open={showResetConfirm} onOpenChange={(open) => { if (!open) handleResetCancel(); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Reset conversation?</AlertDialogTitle>
            </AlertDialogHeader>
            <AlertDialogDescription>
              This will clear all messages and start a new session. The current conversation context will be lost.
            </AlertDialogDescription>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={handleResetCancel}>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleResetConfirm} data-testid="ai-chat-reset-confirm">
                Reset
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Switch adapter confirmation dialog */}
        <AiChatSwitchConfirmDialog
          open={!!switchTarget}
          targetName={switchTarget?.displayName ?? ''}
          onConfirm={handleSwitchConfirm}
          onCancel={handleSwitchCancel}
        />
      </div>
    </div>
  );
}

/** Browser-mode placeholder shown instead of the chat UI. */
function AiChatBrowserModeCTA() {
  return (
    <div
      className="flex-1 flex flex-col items-center justify-center px-4 py-6 text-center gap-4"
      data-testid="ai-chat-browser-mode-cta"
    >
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
        <Terminal className="h-5 w-5 text-muted-foreground" />
      </div>

      <div className="space-y-1.5">
        <p className="text-sm font-medium">AI Chat requires the Desktop App</p>
        <p className="text-xs text-muted-foreground">
          AI Chat is powered by your local{' '}
          <span className="font-medium text-foreground">Claude Code CLI</span>,
          so your existing API keys, settings, and permissions are reused automatically.
        </p>
      </div>

      <ul className="space-y-1.5 text-left w-full">
        {[
          'Uses your local Claude Code setup',
          'AI-assisted SQL writing and debugging',
          '@-mention tables for context-aware help',
        ].map((benefit) => (
          <li
            key={benefit}
            className="flex items-start gap-2 text-xs text-muted-foreground"
          >
            <Monitor className="h-3.5 w-3.5 mt-0.5 shrink-0 text-primary" />
            <span>{benefit}</span>
          </li>
        ))}
      </ul>

      <Button asChild size="sm" className="w-full mt-2" data-testid="ai-chat-download-cta">
        <a href="https://jamsql.com/?utm_source=app&utm_medium=referral&utm_campaign=ai-chat-sidebar#download" target="_blank" rel="noopener noreferrer">
          <Download className="h-4 w-4 mr-2" />
          Download {PRODUCT_NAME}
        </a>
      </Button>
    </div>
  );
}
